import { extractApiErrorMessage, useGetTtaAuditViewQuery, useRunTtaAuditMutation } from "@ots-frontend/ots-data";
import type { ReactNode } from "react";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import { type AuditWarning, latestAuditResult } from "../audit-utils";
import { AuditPanel } from "../AuditPanel";
import { TicketAuditDialog } from "../TicketAuditDialog";

export type ConnectedTicketAuditPanelProps = {
  ttaId: string;
  tenantCode?: string;
  /** The ticket's data checks, e.g. its driver activity warnings. */
  warnings?: AuditWarning[];
  /** Offers Run Audit; leave off for people who may only look. */
  allowRun?: boolean;
  /** After Run Audit finishes, e.g. so a grid can refresh the ticket's audit summary. */
  onAuditComplete?: () => void;
  /** Adds View, for the full audit view. */
  onView?: () => void;
};

/** A ticket's audit, fetched when shown (`GET /ttas/{id}/audit`), with Refresh and Run Audit as in WaterGo. */
export const ConnectedTicketAuditPanel = ({ ttaId, tenantCode, warnings, allowRun = false, onAuditComplete, onView }: ConnectedTicketAuditPanelProps) => {
  const { data, isFetching, error, refetch } = useGetTtaAuditViewQuery({ id: ttaId, tenantCode });
  const [runAudit, run] = useRunTtaAuditMutation();
  const failure = error ?? run.error;
  const { scores, findings } = latestAuditResult(data);

  return (
    <AuditPanel
      scores={data ? scores : undefined}
      findings={data ? findings : undefined}
      warnings={warnings}
      isLoading={isFetching}
      isRunning={run.isLoading}
      error={failure ? extractApiErrorMessage(failure) ?? "Something went wrong." : undefined}
      onRefresh={refetch}
      onRunAudit={allowRun ? () => {
        runAudit({ id: ttaId, tenantCode }).unwrap().then(() => onAuditComplete?.(), () => undefined);
      } : undefined}
      onView={onView}/>
  );
};

export type ConnectedTicketAuditDialogProps = ConnectedTicketAuditPanelProps & {
  open: boolean;
  onClose: () => void;
  subtitle?: ReactNode;
  compact?: boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** The Ticket Audit dialog with its audit fetched by TTA: what the dashboard and WaterGo bridges mount. */
export const ConnectedTicketAuditDialog = ({ open, onClose, subtitle, compact, brand, colorScheme, ...panel }: ConnectedTicketAuditDialogProps) => (
  <TicketAuditDialog open={open} onClose={onClose} subtitle={subtitle} compact={compact} brand={brand} colorScheme={colorScheme}>
    {open && <ConnectedTicketAuditPanel {...panel}/>}
  </TicketAuditDialog>
);
